import React, { useEffect, useMemo, useState } from "react";
import PopUpComponent from "../../components/PopUp/PopUpComponent";
import CloseLogo from "../../Assets/close_icon.png";
import { postAxios } from "../../util/apiCall";
import { BagianConstant } from "../../util/constant";
import { hasRole } from "../../util/utilFunction";
import "./UnsafeConditionPopup.css";

const STATUS_LABEL = {
  OPEN: "Open",
  IN_PROGRESS: "In Progress",
  DONE: "Selesai",
};
const STEPS = ["OPEN", "IN_PROGRESS", "DONE"];

const formatTanggal = (v, withTime) => {
  if (!v) return "-";
  const d = new Date(v);
  if (isNaN(d)) return v;
  return withTime
    ? d.toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" })
    : d.toLocaleDateString("id-ID", { dateStyle: "medium" });
};

// kompres foto supaya payload tidak terlalu besar
const fileToDataUrl = (file, maxSize = 1280, quality = 0.75) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas
          .getContext("2d")
          .drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

const ACTIVITY_ICON = {
  created: "Ticket dibuat",
  comment: "Komentar",
  request_help: "Minta bantuan",
  set_due_date: "Due date",
  upload_after: "Foto after",
  approve: "Approval",
  finish: "Selesai",
  edit: "Diedit",
};

const UnsafeConditionPopup = ({ onClose }) => {
  const dataUser = JSON.parse(localStorage.getItem("auth"));
  // ASUMSI: bagian user ada di dataUser.bagian. Sesuaikan dengan field aslinya.
  const userBagian = dataUser.bagian || dataUser.department || "";
  const isSuper = hasRole(dataUser, "super_user");
  const isAdmin = hasRole(dataUser, "uc_admin");

  const [tickets, setTickets] = useState([]);
  const [reloadKey, setReloadKey] = useState(0);
  const [view, setView] = useState("list"); // list | form | detail
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const seenKey = `uc_seen_${dataUser.id}`;
  const [seen, setSeen] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(seenKey));
    } catch {
      return null; // null = belum pernah diinisialisasi
    }
  });
  const [loaded, setLoaded] = useState(false);

  const call = (body, cb) =>
    postAxios(
      `${process.env.REACT_APP_ENDPOINT}/unsafeCondition`,
      { tim: dataUser.tim, requestor: dataUser.id, ...body },
      dataUser.auth,
      cb,
      ""
    );

  useEffect(() => {
    call({ type: "get" }, (res) => {
      setTickets(Array.isArray(res) ? res : []);
      setLoaded(true);
    });
    // eslint-disable-next-line
  }, [reloadKey]);

  const refresh = () => setReloadKey((k) => k + 1);

  /* ------------------------- hak akses ------------------------- */
  // pemilik: hanya uc_admin (super_user / pembuat / bagian yang sama)
  const isOwner = (t) =>
    isAdmin &&
    (isSuper || t.createdBy?.id === dataUser.id || t.bagian === userBagian);
  // bagian yang dimintai bantuan: boleh lihat + approve (tidak perlu uc_admin)
  const isHelper = (t) =>
    !isOwner(t) &&
    !!userBagian &&
    (t.helpers || []).some((h) => h.bagian === userBagian);
  const canView = (t) =>
    isSuper || isOwner(t) || isHelper(t) || t.createdBy?.id === dataUser.id;

  const visibleTickets = useMemo(() => {
    return tickets.filter(canView).sort((a, b) => {
      const aDone = a.status === "DONE" ? 1 : 0;
      const bDone = b.status === "DONE" ? 1 : 0;
      if (aDone !== bDone) return aDone - bDone; // belum selesai di atas
      return Number(b.noTicket) - Number(a.noTicket); // terbaru di atas
    });
    // eslint-disable-next-line
  }, [tickets]);

  // preview nomor berikutnya (angka final tetap dari backend)
  const nextNo =
    tickets.reduce((max, t) => Math.max(max, Number(t.noTicket) || 0), 0) + 1;

  const selected = tickets.find((t) => t.id === selectedId);

  // simpan ke localStorage
  useEffect(() => {
    if (!seen) return;
    try {
      localStorage.setItem(seenKey, JSON.stringify(seen));
    } catch {}
  }, [seen, seenKey]);

  // kunjungan pertama: anggap semua ticket yang ada sudah dibaca
  useEffect(() => {
    if (!loaded || seen !== null) return;
    const init = {};
    tickets.forEach((t) => {
      init[t.id] = t.updatedAt;
    });
    setSeen(init);
  }, [loaded, tickets, seen]);

  // ticket yang sedang dibuka di halaman detail selalu dianggap sudah dibaca
  useEffect(() => {
    if (!seen || view !== "detail" || !selectedId) return;
    const t = tickets.find((x) => x.id === selectedId);
    if (t && seen[selectedId] !== t.updatedAt) {
      setSeen((prev) => ({ ...prev, [selectedId]: t.updatedAt }));
    }
  }, [view, selectedId, tickets, seen]);

  const lastActivity = (t) =>
    (t.activities || []).reduce(
      (m, a) => (!m || new Date(a.at) > new Date(m.at) ? a : m),
      null
    );

  const isUnread = (t) => {
    if (!seen || !loaded) return false;
    if (view === "detail" && t.id === selectedId) return false;
    if (!seen[t.id]) return t.createdBy?.id !== dataUser.id;
    return new Date(t.updatedAt) > new Date(seen[t.id]);
  };

  const unreadTotal = visibleTickets.filter(isUnread).length;

  /* ------------------------- form buat ticket ------------------------- */
  const emptyForm = {
    tanggalTemuan: new Date().toISOString().slice(0, 10),
    penjelasan: "",
    potensiBahaya: "",
    bagian: "",
    fotoBefore: "",
  };
  const [form, setForm] = useState(emptyForm);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handlePhoto = async (e, setter) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    try {
      setter(await fileToDataUrl(file));
    } catch (err) {
      alert("Foto gagal dibaca. Coba pilih foto lain.");
    }
  };

  const formValid =
    form.tanggalTemuan &&
    form.penjelasan.trim() &&
    form.potensiBahaya.trim() &&
    form.bagian &&
    form.fotoBefore;

  const handleCreate = () => {
    if (!formValid || saving) return;
    setSaving(true);
    call({ type: "create", noTicket: nextNo, ...form }, () => {
      setSaving(false);
      setForm(emptyForm);
      setView("list");
      refresh();
    });
  };

  /* ------------------------- aksi di detail ------------------------- */
  const [comment, setComment] = useState("");
  const [wantHelp, setWantHelp] = useState(false);
  const [helpBagian, setHelpBagian] = useState("");
  const [dueDate, setDueDate] = useState("");

  const doAction = (payload, after) => {
    if (saving) return;
    setSaving(true);
    call({ type: "action", id: selected.id, ...payload }, () => {
      setSaving(false);
      if (after) after();
      refresh();
    });
  };

  const sendComment = () => {
    if (!comment.trim()) return;
    doAction({ action: "comment", text: comment.trim() }, () => setComment(""));
  };

  const sendHelp = () => {
    if (!helpBagian) return;
    doAction({ action: "request_help", bagian: helpBagian }, () => {
      setHelpBagian("");
      setWantHelp(false);
    });
  };

  const sendDueDate = () => {
    if (!dueDate) return;
    doAction({ action: "set_due_date", dueDate }, () => setDueDate(""));
  };

  const sendAfterPhoto = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    try {
      const fotoAfter = await fileToDataUrl(file);
      doAction({ action: "upload_after", fotoAfter });
    } catch (err) {
      alert("Foto gagal dibaca. Coba pilih foto lain.");
    }
    e.target.value = "";
  };

  const allApproved = (t) => (t.helpers || []).every((h) => h.approved);
  const canFinish = (t) =>
    isOwner(t) && t.status !== "DONE" && t.fotoAfter && allApproved(t);

  const openDetail = (t) => {
    setSelectedId(t.id);
    setComment("");
    setWantHelp(false);
    setHelpBagian("");
    setDueDate(t.dueDate ? String(t.dueDate).slice(0, 10) : "");
    setView("detail");
  };

  const handleClose = () => {
    setView("list");
    setSelectedId(null);
    onClose();
  };

  /* ------------------------- render ------------------------- */
  const header = (title, back) => (
    <div className="uc_header">
      <div className="uc_header_left">
        {back && (
          <button className="uc_btn_text" onClick={() => setView("list")}>
            Kembali
          </button>
        )}
        <h3 className="uc_title">{title}</h3>
      </div>
      <img
        className="uc_close"
        src={CloseLogo}
        alt="tutup"
        onClick={handleClose}
      />
    </div>
  );

  const renderList = () => (
    <>
      {header("Unsafe Condition")}
      <div className="uc_toolbar">
        <span className="uc_muted">
          Ticket berikutnya: #{nextNo}
          {unreadTotal > 0 && (
            <span className="uc_count_unread">{unreadTotal} baru</span>
          )}
        </span>
        <button className="uc_btn_primary" onClick={() => setView("form")}>
          Buat ticket
        </button>
      </div>
      <div className="uc_list">
        {visibleTickets.length === 0 && (
          <p className="uc_empty">
            Belum ada ticket. Klik "Buat ticket" untuk melaporkan temuan
            pertama.
          </p>
        )}
        {visibleTickets.map((t, i) => {
          const unread = isUnread(t);
          const last = lastActivity(t);
          const unreadLabel = last
            ? ACTIVITY_ICON[last.type] || "Ada update"
            : "Ada update";
          return (
            <div
              key={t.id}
              className={`uc_card uc_card_${t.status}${
                unread ? " uc_card_unread" : ""
              }`}
              style={{ animationDelay: `${Math.min(i, 8) * 40}ms` }}
              onClick={() => openDetail(t)}
            >
              <div className="uc_card_top">
                <div className="uc_card_id">
                  <strong>
                    {unread && <span className="uc_unread_dot" />}#{t.noTicket}
                  </strong>
                  <span className="uc_chip">{t.bagian}</span>
                  {unread && (
                    <span className="uc_unread_tag">{unreadLabel}</span>
                  )}
                  {isHelper(t) && t.status !== "DONE" && (
                    <span className="uc_tag_help">Diminta bantuan</span>
                  )}
                </div>
                <span className={`uc_badge uc_badge_${t.status}`}>
                  {STATUS_LABEL[t.status]}
                </span>
              </div>

              <p className="uc_card_desc">{t.penjelasan}</p>

              <div className="uc_meta">
                <div className="uc_meta_item">
                  <span className="uc_meta_label">Tanggal temuan</span>
                  <span>{formatTanggal(t.tanggalTemuan)}</span>
                </div>
                <div className="uc_meta_item">
                  <span className="uc_meta_label">Dibuat</span>
                  <span>{formatTanggal(t.createdAt, true)}</span>
                </div>
                <div className="uc_meta_item">
                  <span className="uc_meta_label">Due date</span>
                  <span
                    className={
                      t.dueDate &&
                      t.status !== "DONE" &&
                      new Date(t.dueDate) < new Date(new Date().toDateString())
                        ? "uc_overdue"
                        : ""
                    }
                  >
                    {t.dueDate ? formatTanggal(t.dueDate) : "-"}
                  </span>
                </div>
                <div className="uc_meta_item">
                  <span className="uc_meta_label">Update terakhir</span>
                  <span>{formatTanggal(t.updatedAt, true)}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );

  const renderForm = () => (
    <>
      {header(`Ticket baru #${nextNo}`, true)}
      <div className="uc_form">
        <label>Tanggal temuan</label>
        <input
          type="date"
          value={form.tanggalTemuan}
          onChange={(e) => setField("tanggalTemuan", e.target.value)}
        />

        <label>Bagian ditemukan</label>
        <select
          value={form.bagian}
          onChange={(e) => setField("bagian", e.target.value)}
        >
          <option value="">Pilih bagian</option>
          {BagianConstant.map((b) => (
            <option key={b.value} value={b.value}>
              {b.name}
            </option>
          ))}
        </select>

        <label>Penjelasan temuan unsafe condition</label>
        <textarea
          rows={3}
          value={form.penjelasan}
          onChange={(e) => setField("penjelasan", e.target.value)}
        />

        <label>Potensi bahaya</label>
        <textarea
          rows={3}
          value={form.potensiBahaya}
          onChange={(e) => setField("potensiBahaya", e.target.value)}
        />

        <label>Foto before</label>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => handlePhoto(e, (v) => setField("fotoBefore", v))}
        />
        {form.fotoBefore && (
          <img
            className="uc_thumb"
            src={form.fotoBefore}
            alt="before"
            onClick={() => setPreview(form.fotoBefore)}
          />
        )}

        <div className="uc_actions_end">
          <button
            className="uc_btn_primary"
            disabled={!formValid || saving}
            onClick={handleCreate}
          >
            {saving ? "Menyimpan..." : "Simpan ticket"}
          </button>
        </div>
      </div>
    </>
  );

  const renderDetail = () => {
    if (!selected) return null;
    const t = selected;
    const owner = isOwner(t);
    const helper = isHelper(t);
    const done = t.status === "DONE";
    const myHelp = (t.helpers || []).find((h) => h.bagian === userBagian);
    const availableHelpBagian = BagianConstant.filter(
      (b) =>
        b.value !== t.bagian &&
        !(t.helpers || []).some((h) => h.bagian === b.value)
    );
    const activities = [...(t.activities || [])].sort(
      (a, b) => new Date(b.at) - new Date(a.at)
    );

    return (
      <>
        {header(`Ticket #${t.noTicket}`, true)}

        {!owner && !helper && (
          <p className="uc_muted" style={{ margin: "0 0 10px 0" }}>
            Anda hanya dapat melihat ticket ini.
          </p>
        )}

        {/* alur status ala board */}
        <div className="uc_steps">
          {STEPS.map((s) => (
            <div
              key={s}
              className={`uc_step ${
                STEPS.indexOf(s) <= STEPS.indexOf(t.status) ? "uc_step_on" : ""
              }`}
            >
              {STATUS_LABEL[s]}
            </div>
          ))}
        </div>

        <div className="uc_detail_grid">
          <div>
            <p className="uc_label">Tanggal temuan</p>
            <p>{formatTanggal(t.tanggalTemuan)}</p>
            <p className="uc_label">Bagian ditemukan</p>
            <p>{t.bagian}</p>
            <p className="uc_label">Dibuat oleh</p>
            <p>
              {t.createdBy?.name || "-"} · {formatTanggal(t.createdAt, true)}
            </p>
            <p className="uc_label">Due date</p>
            <p>{t.dueDate ? formatTanggal(t.dueDate) : "Belum ditentukan"}</p>
          </div>
          <div>
            <p className="uc_label">Penjelasan temuan</p>
            <p>{t.penjelasan}</p>
            <p className="uc_label">Potensi bahaya</p>
            <p>{t.potensiBahaya}</p>
          </div>
        </div>

        <div className="uc_photos">
          <div>
            <p className="uc_label">Foto before</p>
            {t.fotoBefore ? (
              <img
                className="uc_thumb"
                src={t.fotoBefore}
                alt="before"
                onClick={() => setPreview(t.fotoBefore)}
              />
            ) : (
              <p className="uc_muted">-</p>
            )}
          </div>
          <div>
            <p className="uc_label">Foto after</p>
            {t.fotoAfter ? (
              <img
                className="uc_thumb"
                src={t.fotoAfter}
                alt="after"
                onClick={() => setPreview(t.fotoAfter)}
              />
            ) : (
              <p className="uc_muted">Belum ada</p>
            )}
          </div>
        </div>

        {/* bantuan */}
        {(t.helpers || []).length > 0 && (
          <div className="uc_section">
            <p className="uc_label">Bagian yang dimintai bantuan</p>
            {t.helpers.map((h) => (
              <div key={h.bagian} className="uc_helper_row">
                <span>{h.bagian}</span>
                <span
                  className={
                    h.approved
                      ? "uc_badge uc_badge_DONE"
                      : "uc_badge uc_badge_OPEN"
                  }
                >
                  {h.approved
                    ? `Selesai disetujui ${formatTanggal(h.approvedAt, true)}`
                    : "Menunggu approval selesai"}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* aksi pemilik ticket (uc_admin) */}
        {owner && !done && (
          <div className="uc_section">
            <p className="uc_label">Tindak lanjut</p>

            <div className="uc_row">
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
              <button
                className="uc_btn_secondary"
                disabled={!dueDate || saving}
                onClick={sendDueDate}
              >
                Simpan due date
              </button>
            </div>

            <div className="uc_row">
              <label
                className="uc_btn_secondary uc_file_btn"
                style={{
                  background: "orangered",
                  color: "white",
                  border: "none",
                }}
              >
                {t.fotoAfter ? "Ganti foto after" : "Upload foto after"}
                <input
                  type="file"
                  accept="image/*"
                  onChange={sendAfterPhoto}
                  hidden
                />
              </label>
            </div>

            <label className="uc_check">
              <input
                type="checkbox"
                checked={wantHelp}
                onChange={(e) => setWantHelp(e.target.checked)}
              />
              Minta bantuan bagian lain
            </label>
            {wantHelp && (
              <div className="uc_row">
                <select
                  value={helpBagian}
                  onChange={(e) => setHelpBagian(e.target.value)}
                >
                  <option value="">Pilih bagian</option>
                  {availableHelpBagian.map((b) => (
                    <option key={b.value} value={b.value}>
                      {b.name}
                    </option>
                  ))}
                </select>
                <button
                  className="uc_btn_secondary"
                  disabled={!helpBagian || saving}
                  onClick={sendHelp}
                >
                  Kirim permintaan
                </button>
              </div>
            )}

            <div className="uc_actions_end">
              <button
                className="uc_btn_primary"
                disabled={!canFinish(t) || saving}
                onClick={() => doAction({ action: "finish" })}
              >
                Tandai selesai
              </button>
            </div>
            {!canFinish(t) && (
              <p className="uc_muted">
                Ticket bisa diselesaikan setelah ada foto after
                {(t.helpers || []).length > 0
                  ? " dan semua bagian yang dimintai bantuan sudah approve selesai"
                  : ""}
                .
              </p>
            )}
          </div>
        )}

        {/* aksi bagian yang dimintai bantuan: approval (+ komentar bila uc_admin) */}
        {helper && !done && myHelp && !myHelp.approved && (
          <div className="uc_section">
            <p className="uc_label">
              Bagian {userBagian} diminta membantu ticket ini
            </p>
            <button
              className="uc_btn_primary"
              disabled={saving || !t.fotoAfter}
              onClick={() =>
                doAction({ action: "approve", bagian: userBagian })
              }
            >
              Approve selesai
            </button>
            {!t.fotoAfter && (
              <p className="uc_muted">
                Approve selesai bisa dilakukan setelah pemilik ticket mengunggah
                foto after.
                {isAdmin && " Sementara itu Anda bisa memberi komentar."}
              </p>
            )}
          </div>
        )}

        {/* komentar (pemilik dan helper, khusus uc_admin) */}
        {(owner || (helper && isAdmin)) && !done && (
          <div className="uc_section">
            <p className="uc_label">Komentar</p>
            <textarea
              rows={2}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Tulis komentar"
            />
            <div className="uc_actions_end">
              <button
                className="uc_btn_secondary"
                disabled={!comment.trim() || saving}
                onClick={sendComment}
              >
                Kirim komentar
              </button>
            </div>
          </div>
        )}

        {/* riwayat pergerakan */}
        <div className="uc_section">
          <p className="uc_label">Riwayat pergerakan</p>
          <div className="uc_timeline">
            {activities.length === 0 && (
              <p className="uc_muted">Belum ada aktivitas.</p>
            )}
            {activities.map((a, i) => (
              <div key={i} className="uc_tl_item">
                <div className="uc_tl_dot" />
                <div>
                  <p className="uc_tl_head">
                    {ACTIVITY_ICON[a.type] || a.type}
                    <span className="uc_muted">
                      {" "}
                      · {a.by || "-"} · {formatTanggal(a.at, true)}
                    </span>
                  </p>
                  {a.text && <p className="uc_tl_text">{a.text}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      <PopUpComponent style={{ marginTop: "3vh" }}>
        <div className="uc_wrapper">
          <div key={view} className="uc_view">
            {view === "list" && renderList()}
            {view === "form" && renderForm()}
            {view === "detail" && renderDetail()}
          </div>
        </div>
      </PopUpComponent>
      {preview && (
        <div className="uc_lightbox" onClick={() => setPreview(null)}>
          <img src={preview} alt="preview" />
        </div>
      )}
    </>
  );
};

export default UnsafeConditionPopup;
